import React, { useState, useEffect, useRef } from 'react';
import './FundCentreMasterPage.css';
import { API_BASE_URL, api } from '../services/api';
import FundCentreWorldMap from '../components/FundCentreWorldMap';
import { getCityCoordinates, getCityIANATimezone } from '../utils/cityCoordinates';

// GMT Timezones
const GMT_TIMEZONES = [
  'GMT+0',
  'GMT+1',
  'GMT+2',
  'GMT+3',
  'GMT+4',
  'GMT+5',
  'GMT+5:30',
  'GMT+6',
  'GMT+7',
  'GMT+8',
  'GMT+9',
  'GMT+10',
  'GMT+11',
  'GMT+12',
  'GMT-1',
  'GMT-2',
  'GMT-3',
  'GMT-4',
  'GMT-5',
  'GMT-6',
  'GMT-7',
  'GMT-8',
  'GMT-9',
  'GMT-10',
  'GMT-11',
  'GMT-12'
];

// Countries list including Sri Lanka
const COUNTRIES = [
  'Afghanistan',
  'Albania',
  'Algeria',
  'Argentina',
  'Australia',
  'Austria',
  'Bangladesh',
  'Belgium',
  'Brazil',
  'Canada',
  'China',
  'Denmark',
  'Egypt',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Italy',
  'Japan',
  'Kenya',
  'Malaysia',
  'Mexico',
  'Netherlands',
  'New Zealand',
  'Nigeria',
  'Norway',
  'Pakistan',
  'Philippines',
  'Poland',
  'Portugal',
  'Russia',
  'Singapore',
  'South Africa',
  'South Korea',
  'Spain',
  'Sri Lanka',
  'Sweden',
  'Switzerland',
  'Thailand',
  'Turkey',
  'United Kingdom',
  'United States',
  'Vietnam'
];

// Common currencies
const CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'CNY', 'INR', 'LKR', 'SGD', 'THB', 'MYR', 'IDR', 'PHP', 'VND', 'KRW', 'HKD', 'NZD', 'ZAR', 'BRL', 'MXN', 'RUB', 'AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD'
];

const CITIES = [
  'New York', 'Chicago', 'London', 'Frankfurt', 'Paris', 'Zurich', 'Madrid',
  'Dubai', 'Mumbai', 'Colombo', 'Singapore', 'Hong Kong', 'Bangkok', 'Tokyo',
  'Seoul', 'Sydney', 'Melbourne', 'Johannesburg', 'Cairo', 'Toronto', 'Vancouver', 'São Paulo'
];

const IANA_TIMEZONES = [
  'America/New_York', 'America/Chicago', 'Europe/London', 'Europe/Berlin', 'Europe/Paris',
  'Europe/Zurich', 'Europe/Madrid', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Colombo',
  'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Bangkok', 'Asia/Tokyo', 'Asia/Seoul',
  'Australia/Sydney', 'Australia/Melbourne', 'Africa/Johannesburg', 'Africa/Cairo',
  'America/Toronto', 'America/Vancouver', 'America/Sao_Paulo'
];

const initialState = {
  name: '',
  fund_centre_code: '',
  country: '',
  city: '',
  gmt_timezone: '',
  iana_timezone: '',
  latitude: '',
  longitude: '',
  dst_observed: 'N',
  currency: ''
};

function FundCentreMasterPage() {
  const [tab, setTab] = useState('add');
  const [fundCentres, setFundCentres] = useState([]);
  const [form, setForm] = useState(initialState);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFundCentreId, setSelectedFundCentreId] = useState(null);
  const tableRowRefs = useRef({});

  useEffect(() => {
    fetchFundCentres();
  }, []);

  function initialForm() {
    return { ...initialState };
  }

  async function fetchFundCentres() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`${API_BASE_URL}/fund-centre-master`);
      if (res.data.success) {
        setFundCentres(res.data.data || []);
      } else {
        setError('Failed to fetch fund centres');
      }
    } catch (e) {
      console.error('Error fetching fund centres:', e);
      setError(e.response?.data?.error || 'Failed to fetch fund centres');
    }
    setLoading(false);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    
    // Auto-format fund centre code to start with FC
    if (name === 'fund_centre_code') {
      let formattedValue = value.toUpperCase();
      if (!formattedValue.startsWith('FC')) {
        formattedValue = 'FC' + formattedValue.replace(/^FC/i, '');
      }
      // Only allow alphanumeric after FC
      formattedValue = 'FC' + formattedValue.substring(2).replace(/[^0-9]/g, '');
      setForm(f => ({ ...f, [name]: formattedValue }));
    } else if (name === 'city') {
      // Auto-populate coordinates and IANA timezone when city is selected
      const coords = getCityCoordinates(value);
      const ianaTz = getCityIANATimezone(value);
      setForm(f => ({
        ...f,
        [name]: value,
        latitude: coords ? coords.lat.toString() : '',
        longitude: coords ? coords.lng.toString() : '',
        iana_timezone: ianaTz || ''
      }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
    setError('');
    setSuccess('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!form.name.trim()) {
      setError('Name is required');
      setLoading(false);
      return;
    }
    if (!form.fund_centre_code || !form.fund_centre_code.startsWith('FC')) {
      setError('Fund centre code must start with "FC"');
      setLoading(false);
      return;
    }
    if (!form.country) {
      setError('Country is required');
      setLoading(false);
      return;
    }
    if (!form.gmt_timezone) {
      setError('GMT Timezone is required');
      setLoading(false);
      return;
    }
    if (!form.currency) {
      setError('Currency is required');
      setLoading(false);
      return;
    }

    try {
      if (editId) {
        await api.put(`${API_BASE_URL}/fund-centre-master/${editId}`, form);
        setSuccess('Fund centre updated successfully');
        setShowEdit(false);
      } else {
        await api.post(`${API_BASE_URL}/fund-centre-master`, form);
        setSuccess('Fund centre created successfully');
      }
      setForm(initialForm());
      setEditId(null);
      fetchFundCentres();
    } catch (e) {
      console.error('Error saving fund centre:', e);
      setError(e.response?.data?.error || 'Save failed');
    }
    setLoading(false);
  }

  function startEdit(fundCentre) {
    setEditId(fundCentre.id);
    setForm({
      name: fundCentre.name || '',
      fund_centre_code: fundCentre.fund_centre_code || '',
      country: fundCentre.country || '',
      city: fundCentre.city || '',
      gmt_timezone: fundCentre.gmt_timezone || '',
      iana_timezone: fundCentre.iana_timezone || '',
      latitude: fundCentre.latitude ? fundCentre.latitude.toString() : '',
      longitude: fundCentre.longitude ? fundCentre.longitude.toString() : '',
      dst_observed: fundCentre.dst_observed || 'N',
      currency: fundCentre.currency || ''
    });
    setShowEdit(true);
    setTab('add');
    setError('');
    setSuccess('');
  }

  function handleMarkerClick(fundCentre) {
    setSelectedFundCentreId(fundCentre.id);
    // Scroll to table row if it exists
    if (tableRowRefs.current[fundCentre.id]) {
      tableRowRefs.current[fundCentre.id].scrollIntoView({ behavior: 'smooth', block: 'center' });
      tableRowRefs.current[fundCentre.id].classList.add('highlight-row');
      setTimeout(() => {
        if (tableRowRefs.current[fundCentre.id]) {
          tableRowRefs.current[fundCentre.id].classList.remove('highlight-row');
        }
      }, 2000);
    }
  }

  function handleTableRowClick(fundCentre) {
    setSelectedFundCentreId(fundCentre.id);
  }

  async function handleDelete(id) {
    if (!window.confirm('Are you sure you want to delete this fund centre?')) return;
    setLoading(true);
    try {
      await api.delete(`${API_BASE_URL}/fund-centre-master/${id}`);
      setSuccess('Fund centre deleted successfully');
      fetchFundCentres();
    } catch (e) {
      console.error('Error deleting fund centre:', e);
      setError(e.response?.data?.error || 'Delete failed');
    }
    setLoading(false);
  }

  const filteredFundCentres = search
    ? fundCentres.filter(
        fc =>
          fc.name?.toLowerCase().includes(search.toLowerCase()) ||
          fc.fund_centre_code?.toLowerCase().includes(search.toLowerCase()) ||
          fc.country?.toLowerCase().includes(search.toLowerCase())
      )
    : fundCentres;

  return (
    <div className="fund-centre-master-page ui-page">
      {success && (
        <div className="success-message">{success}</div>
      )}
      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="tabs">
        <button
          className={tab === 'add' ? 'active' : ''}
          onClick={() => {
            setTab('add');
            setShowEdit(false);
            setForm(initialForm());
            setEditId(null);
            setError('');
            setSuccess('');
          }}
        >
          Add Fund Centre
        </button>
        <button
          className={tab === 'search' ? 'active' : ''}
          onClick={() => {
            setTab('search');
            setError('');
            setSuccess('');
          }}
        >
          Search Fund Centre
        </button>
      </div>

      {tab === 'add' && (
        <div className="add-fund-centre-container">
          <form className="master-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Name <span className="required">*</span></label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter fund centre name"
                required
              />
            </div>
            <div className="form-group">
              <label>Fund Centre Code <span className="required">*</span></label>
              <input
                name="fund_centre_code"
                value={form.fund_centre_code}
                onChange={handleChange}
                placeholder="FC123"
                required
                maxLength={20}
              />
              <small className="hint">Must start with "FC" followed by numbers</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Country <span className="required">*</span></label>
              <select
                name="country"
                value={form.country}
                onChange={handleChange}
                required
              >
                <option value="">Select Country</option>
                {COUNTRIES.map(country => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>City</label>
              <select
                name="city"
                value={form.city}
                onChange={handleChange}
              >
                <option value="">Select City</option>
                {CITIES.map(city => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
              <small className="hint">Selecting a city will auto-populate coordinates and IANA timezone</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>GMT Timezone <span className="required">*</span></label>
              <select
                name="gmt_timezone"
                value={form.gmt_timezone}
                onChange={handleChange}
                required
              >
                <option value="">Select GMT Timezone</option>
                {GMT_TIMEZONES.map(tz => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>IANA Timezone</label>
              <select
                name="iana_timezone"
                value={form.iana_timezone}
                onChange={handleChange}
              >
                <option value="">Select IANA Timezone</option>
                {IANA_TIMEZONES.map(tz => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Latitude</label>
              <input
                name="latitude"
                type="number"
                step="any"
                value={form.latitude}
                onChange={handleChange}
                placeholder="e.g., 6.9271"
              />
            </div>
            <div className="form-group">
              <label>Longitude</label>
              <input
                name="longitude"
                type="number"
                step="any"
                value={form.longitude}
                onChange={handleChange}
                placeholder="e.g., 79.8612"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>DST Observed</label>
              <select
                name="dst_observed"
                value={form.dst_observed}
                onChange={handleChange}
              >
                <option value="N">No</option>
                <option value="Y">Yes</option>
              </select>
            </div>
            <div className="form-group">
              <label>Currency <span className="required">*</span></label>
              <select
                name="currency"
                value={form.currency}
                onChange={handleChange}
                required
              >
                <option value="">Select Currency</option>
                {CURRENCIES.map(currency => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" disabled={loading} className="submit-btn">
              {editId ? 'Update' : 'Add'} Fund Centre
            </button>
            {showEdit && (
              <button
                type="button"
                onClick={() => {
                  setShowEdit(false);
                  setEditId(null);
                  setForm(initialForm());
                  setError('');
                  setSuccess('');
                }}
                className="cancel-btn"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
        
        <div className="map-integrated">
          <FundCentreWorldMap
            fundCentres={fundCentres}
            selectedFundCentreId={selectedFundCentreId}
            onMarkerClick={handleMarkerClick}
            onTableRowClick={handleTableRowClick}
          />
        </div>
        </div>
      )}

      {tab === 'search' && (
        <div className="search-section">
          <input
            type="text"
            placeholder="Search by name, code, or country..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="search-input"
          />
          {loading && <div className="loading">Loading...</div>}
          <table className="master-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Fund Centre Code</th>
                <th>Country</th>
                <th>City</th>
                <th>GMT Timezone</th>
                <th>Currency</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFundCentres.length === 0 && !loading && (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                    No fund centres found.
                  </td>
                </tr>
              )}
              {filteredFundCentres.map(fc => (
                <tr 
                  key={fc.id}
                  ref={el => tableRowRefs.current[fc.id] = el}
                  onClick={() => handleTableRowClick(fc)}
                  className={selectedFundCentreId === fc.id ? 'selected-row' : ''}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{fc.name}</td>
                  <td>{fc.fund_centre_code}</td>
                  <td>{fc.country}</td>
                  <td>{fc.city || 'N/A'}</td>
                  <td>{fc.gmt_timezone}</td>
                  <td>{fc.currency}</td>
                  <td>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(fc);
                      }}
                      className="edit-btn"
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(fc.id);
                      }}
                      className="delete-btn"
                      title="Delete"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default FundCentreMasterPage;
